import { FilterFilled, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Space } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  BACKEND_DATE_FORMAT,
  BOOKING_TABLE_FIELDS,
  DATE_TIME_FORMAT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROUTES_MODULES_KEY,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import { useTableQueryParams } from '../../hooks/useTableQueryParams';
import { ASSET_BOOKINGS } from './graphql/Queries';

const { RangePicker } = DatePicker;

const buildBookingFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  ...(params?.bookingDate?.from && { startDate: params?.bookingDate?.from }),
  ...(params?.bookingDate?.to && { endDate: params?.bookingDate?.to }),
});

function Booking() {
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: BOOKING_TABLE_FIELDS,
  });
  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.ASSETS_LISTING,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });

  const [executeQuery, { loading, data }] = useLazyQuery(ASSET_BOOKINGS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildBookingFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handlePagination = (page, pageSize) => {
    setParams({ page: pageSize !== params?.pageSize ? 1 : page, pageSize });
  };

  const handleSearch = debounce((value) => {
    setParams({ search: value?.trim(), page: 1 });
  }, 500);

  const handleAssetSearch = debounce((value) => {
    setParams({ search: value?.trim(), page: 1 });
  }, 700);

  const getAssetFilterProps = () => ({
    filterSearch: true,
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <SearchComponent
            key={params?.search || ''}
            defaultValue={params?.search}
            getData={handleAssetSearch}
            name="Asset"
          />
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled className={params?.search ? 'filter-filled-data' : ''} />
    ),
  });

  const onRangeChange = (dates) => {
    setParams({
      bookingDate: dates
        ? {
            from: dayjs(dates?.[0])?.format(BACKEND_DATE_FORMAT),
            to: dayjs(dates?.[1])?.format(BACKEND_DATE_FORMAT),
          }
        : { from: null, to: null },
    });
  };

  const disabledDate = (current) => {
    const currentDate = new Date();
    return current && current?.valueOf() > currentDate?.getTime();
  };
  const columns = [
    {
      title: 'Sr.No',
      align: 'center',
      width: 50,
      render: (_, __, index) => (
        <>{(params?.page - 1) * params?.pageSize + index + 1}</>
      ),
    },
    {
      title: 'Asset ID',
      dataIndex: 'assetId',
      key: 'assetId',
      align: 'center',
      render: (_, record) => record?.asset?.assetId || '-',
    },
    {
      title: 'Asset Name',
      dataIndex: 'assetName',
      key: 'assetName',
      align: 'center',
      render: (_, record) => record?.asset?.assetName || '-',
      ...getAssetFilterProps(),
    },
    {
      title: 'Booked by',
      dataIndex: 'displayName',
      key: 'displayName',
      align: 'center',
      render: (_, record) => record?.user?.displayName || '-',
    },
    {
      title: 'Clock IN',
      dataIndex: 'clockIn',
      key: 'clockIn',
      align: 'center',
      render: (_, record) =>
        record?.assignedAt
          ? dayjs(record?.assignedAt)?.format(DATE_TIME_FORMAT)
          : '-',
    },
    {
      title: 'Clock Out',
      dataIndex: 'clockOut',
      key: 'clockOut',
      align: 'center',
      render: (_, record) =>
        record?.submittedAt
          ? dayjs(record?.submittedAt)?.format(DATE_TIME_FORMAT)
          : '-',
    },
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Mobile Booking and Management Tracker"
        extra={
          <>
            <div className="booking-filter">
              {isCreatePermission && (
                <Button
                  key="1"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    history?.push({
                      pathname: `${ROUTES_MODULES_KEY?.ASSETS_LISTING}/add`,
                    });
                  }}
                >
                  Add New Device
                </Button>
              )}
              <SearchComponent
                key={params?.search || ''}
                defaultValue={params?.search}
                getData={handleSearch}
                name="By Asset Name, Employee Name"
              />
              <RangePicker
                value={
                  params?.bookingDate?.from && params?.bookingDate?.to
                    ? [
                        dayjs(params?.bookingDate?.from, BACKEND_DATE_FORMAT),
                        dayjs(params?.bookingDate?.to, BACKEND_DATE_FORMAT),
                      ]
                    : null
                }
                disabledDate={disabledDate}
                onChange={onRangeChange}
              />
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            className="booking-table"
            columns={columns}
            dataSource={data?.assetBookings?.assetBookings}
            loadingData={loading}
            rowKey={(obj) => obj?.id}
            bordered
            paginationConfig={{
              current: params?.page,
              total: data?.assetBookings?.count,
              pageSize: params?.pageSize,
              onChange: handlePagination,
            }}
          />
        </div>
      </Card>
    </div>
  );
}

export default Booking;
